import React from 'react';

interface ImagePreloaderProps {
  imageUrl: string;
  format?: 'webp' | 'avif' | 'jpeg';
  as?: 'image';
  media?: string;
}

/**
 * Component to preload critical images for better LCP performance
 * Should be used in document head for above-the-fold images
 */
export const ImagePreloader: React.FC<ImagePreloaderProps> = ({
  imageUrl,
  format = 'webp',
  as = 'image',
  media
}) => {
  if (!imageUrl) return null;

  // Clean URL and apply format
  let optimizedUrl = imageUrl.replace(/&(width|height|format)=\d+|&format=\w+/g, '');
  
  // Add format and optimized width for preloading
  if (format === 'webp') {
    optimizedUrl += '&format=webp&width=1920';
  } else if (format === 'avif') {
    optimizedUrl += '&format=avif&width=1920';
  } else {
    optimizedUrl += '&width=1920';
  }

  return (
    <link
      rel="preload"
      as={as}
      href={optimizedUrl}
      {...(media && { media })}
    />
  );
};

/**
 * Hook to inject preload links into document head
 */
export const useImagePreloader = (images: { url: string; priority?: number }[]) => {
  React.useEffect(() => {
    if (!images.length) return;

    const preloadLinks: HTMLLinkElement[] = [];

    // Sort by priority and preload top 3 images
    const priorityImages = images
      .sort((a, b) => (b.priority || 0) - (a.priority || 0))
      .slice(0, 3);

    priorityImages.forEach((image, index) => {
      // Preload AVIF first, then WebP as fallback
      const formats = ['avif', 'webp'];
      
      formats.forEach((format) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        
        let url = image.url.replace(/&(width|height|format)=\d+|&format=\w+/g, '');
        url += `&format=${format}&width=1920`;
        
        link.href = url;
        
        // Add media query for mobile vs desktop
        if (index === 0) {
          link.media = '(max-width: 768px)';
        }
        
        document.head.appendChild(link);
        preloadLinks.push(link);
      });
    });

    // Cleanup on unmount
    return () => {
      preloadLinks.forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, [images]);
};

export default ImagePreloader;
